/**
 *  Isotransformation
 *  from string to `enriched Text`
 *  and from `enriched Text` to string
 */

export const REGEX = {
  // detect the expressions of the form @[n] with an integer
  MENTIONED_USERS_ID: /@\[([0-9]+)]/g,
  // detect emoticons
  // [8:;] - starts with any of these chars
  // ['-]{0,} - optionally has an extra char, like hyphen eg, :-D
  // (&#39;){0,1} - for smiley's with single quote, since single quote is encoded. eg, :'(
  // [dDpPoOxX|/\)\(] - followed by one of these charset: d,D,p,P,o,O,x,X,|,),(
  SMILEYS: /[8:;]['-]{0,}('){0,1}[dDpPoOxX|\/\)\(](?=$|\s)/g,
  // Found on http://daringfireball.net/2010/07/improved_regex_for_matching_urls
  /* eslint-disable */
  URL: /(?:\s|^)((?:(?:f|ht)tps?:\/\/(?:www\.)?|www\.)([-a-zA-Z0-9@:%_\+,~#?;&\/\/=!()]+\.[-a-zA-Z0-9@:%_\+.,~#?;&\/\/=!()]+))/g,
  YOUTUBE_URL: /(?:youtube\.com\/watch\?v=|youtu\.be\/)(.*)/,
  /* eslint-enable */
  BBCODE: /\[\/?(?:b|i|u)]/g,
};
export const WHITE_LIST_URL = [
  'nhs.uk',
  'wikipedia.org',
  'healthunlocked.com',
];

/**
 * @return {string} redirect address of localhost
 */
export function redirectAddress() {
  return window.document ?
    `${document.location.protocol}//${document.location.host}/api/redirect?url=` :
    'https://healthunlocked.com/api/redirect?url=';
}


/**
 * Return two arrays.
 * One containing the substr surrounding the pattern
 * One containing the matched patterned once processed
 * @param {str} string to process
 * @param {pattern} patterns to replace
 * @param {func} function processing the matched pattern to obtained the desired result
 *
 */
export function _split(str: String, { pattern, processing }): Object {
  const escaped = [];
  const replaced = [];

  pattern.lastIndex = 0;
  let lastIndex = pattern.lastIndex;
  let match = pattern.exec(str);
  while (match !== null) {
    escaped.push(str.substring(lastIndex, match.index));
    replaced.push(processing(...match));
    lastIndex = pattern.lastIndex;
    match = pattern.exec(str);
  }
  escaped.push(str.slice(lastIndex));

  return { escaped, replaced };
}

/**
 * Apply successuvely and exclusively the replacement of pattern in a string and an array
 * of <Text> elements
 * @param {str} string to process
 * @param {replacements}
 *
 */
export function _replace({ str, style }, rep): Array {
  const [firstReplacement, ...replacements] = rep;
  if (str.length === 0) {
    return [];
  }
  if (!firstReplacement) {
    return [{ type: 'text', text: str, style }];
  }

  const { escaped, replaced } = _split(str, firstReplacement);

  return escaped
    .map(chunk => _replace({ str: chunk, style }, replacements))
    .reduce((prev, next, index) => [...prev, ...next, replaced[index]], [])
    .filter(item => !!item);
}

/**
 * ensure the url to be complete
 * @param {url} string to process
 *
 */
export function _ensureURL(url: String) {
  const complete = url.slice(0, 8) === 'https://' ||
    url.slice(0, 7) === 'http://' ||
    url.slice(0, 7) === 'ftps://' ||
    url.slice(0, 6) === 'ftp://';
  return (complete ? '' : 'http://') + url;
}

/**
 * @param {url} string to process
 */
export function _shortenUrlForDisplay(url: String) {
  const hasHTTPPrefix = (url.slice(0, 8) === 'https://' && 8) || (url.slice(0, 7) === 'http://' && 7);
  const unPrefixed = hasHTTPPrefix ? url.slice(hasHTTPPrefix) : url;
  return unPrefixed.length > 30 ? (unPrefixed.slice(0, 27) + '...') : unPrefixed;
}

/**
 * replace url with links
 * used as callback of RegExp.prototype.replace
 *
 */
export function _replaceUrl() {
  const matchYoutube = REGEX.YOUTUBE_URL.exec(arguments[2]);
  const youtubeID = matchYoutube && matchYoutube[1];
  const isWhiteList = !youtubeID && WHITE_LIST_URL
    .reduce((prev, white) => prev || (arguments[2].slice(0, white.length) === white), false);
  const space = arguments[0][0] !== arguments[1][0] ? ' ' : '';

  return {
    isWhiteList,
    originalText: arguments[0],
    text: space + _shortenUrlForDisplay(arguments[2]),
    type: 'link',
    url: isWhiteList ?
      _ensureURL(arguments[1]) :
      `${redirectAddress()}${encodeURIComponent(_ensureURL(arguments[1]))}`,
    youtubeID,
  };
}

/**
 * replace carret return
 * used as callback of RegExp.prototype.replace
 *
 */
export function _replaceReturn() {
  return { type: 'return' };
}

/**
 * replace mentions with links
 * used as callback of RegExp.prototype.replace
 *
 */
export function _replaceMentions(data) {
  return function () {
    const user = data.userMap[arguments[1]];
    return user ? {
      type: 'mention',
      userId: user.id,
      username: user.username,
    } : null;
  };
}

/**
 * replace characters with smileys
 * used as callback of RegExp.prototype.replace
 *
 */
export function _replaceSmileys() {
  return { type: 'smiley', originalText: arguments[0] };
}

/**
 * Determine if two style object are equal
 */
export function shallowStyleEquality(styleA, styleB) {
  return (styleA.b === styleB.b) && (styleA.i === styleB.i) && (styleA.u === styleB.u);
}

/**
 * Get the interval of the string with different style according BBcode
 * @param {str} string to process
 *
 * @return [{ size, index, style }]
 *   the size of the bbtag,
 *   the index of end of the bbtag,
 *   the new style
 */
export function getStyleIntervals(str: String) {
  const pattern = REGEX.BBCODE;
  const state = { b: 0, i: 0, u: 0 };
  const styleIntervals = [];

  pattern.lastIndex = 0;
  let match = pattern.exec(str);
  while (match !== null) {
    switch (match[0]) {
      case '[b]':
        state.b += 1;
        break;
      case '[i]':
        state.i += 1;
        break;
      case '[u]':
        state.u += 1;
        break;
      case '[/b]':
        state.b = Math.max(state.b - 1, 0);
        break;
      case '[/i]':
        state.i = Math.max(state.i - 1, 0);
        break;
      case '[/u]':
        state.u = Math.max(state.u - 1, 0);
        break;
    }
    const currentStyle = { b: !!state.b, i: !!state.i, u: !!state.u };
    styleIntervals.push({ style: currentStyle, index: pattern.lastIndex, size: match[0].length });
    match = pattern.exec(str);
  }

  return styleIntervals;
}

/**
 * Get the interval of the string with different style according BBcode
 * @param {str} string to process
 *
 * @return [{ str, style }]
 *   str: ordinate partition of the original str
 *   style: style representation of the text
 */
export function getStyledString(str: String, styleIntervals) {
  const styledString = [];

  [...styleIntervals, { style: {}, index: str.length, size: 0 }]
    .reduce((previousInterval, newInterval) => {
      const partialStr = str.slice(previousInterval.index, newInterval.index - newInterval.size);
      if (!!partialStr.length) {
        styledString.push({ str: partialStr, style: previousInterval.style });
      }
      return newInterval;
    }, { style: {}, index: 0, size: 0 });

  return styledString.reduce((begin, next) => {
    const prev = begin.pop();
    if (!prev) {
      return [next];
    }
    if (shallowStyleEquality(prev.style, next.style)) {
      return [...begin, { str: (prev.str + next.str), style: prev.style }];
    }
    return [...begin, prev, next];
  }, []);
}

/**
 * Strip off HTML tags and replace mentions and urls
 * @param {str} string to process
 * @param {data} supplementary data necessary to process the string
 *
 */
export function parseString(
  str: String = '',
  data: Object,
  // Disabled Replacement types
  { carriageReturn, url, mentionedUsers, smileys } = {}
): Array {
  const styledString = getStyledString(str, getStyleIntervals(str));

  const replacements = [
    !carriageReturn && { pattern: /\r\n|\r|\n/g, processing: _replaceReturn },
    !url && { pattern: REGEX.URL, processing: _replaceUrl },
    !mentionedUsers && { pattern: REGEX.MENTIONED_USERS_ID, processing: _replaceMentions(data) },
    !smileys && { pattern: REGEX.SMILEYS, processing: _replaceSmileys },
  ].filter(x => !!x);

  const result = styledString
    .map(styledStr => _replace(styledStr, replacements).filter(e => !!e))
    .reduce((previousValue, newValue) => [...previousValue, ...newValue], []);

  return result || [];
}


/**
 * Transform an enriched Text to simple String
 * @param {enrichedText} Array to process
 *
 */
export function parseEnrichedText(enrichedText: Array): Array {
  return enrichedText.reduce((prev, item) => {
    switch (item.type) {
      case 'mention':
        return prev + `@[${item.userid}]`;
      case 'link':
        return prev + item.user;
      default:
        return prev + item.text;
    }
  }, '');
}



// WEBPACK FOOTER //
// ./~/virgo/src/utils/parserText.js